// src/Components/UserActivities/DownloadReportModal.jsx

import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    CircularProgress,
    Typography
} from '@mui/material';
import { PictureAsPdf as PdfIcon, TableChart as ExcelIcon } from '@mui/icons-material';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import CustomMonthYearIndividualDatePicker from '../Widgets/CustomDates/CustomMonthYearIndividualDatePicker/CustomMonthYearIndividualDatePicker';
import AttendanceService from '../../Services/attendance.service.js';

export default function DownloadReportModal({ open, onClose, direction, user, token }) {
    const [date, setDate] = useState(new Date());
    const [loading, setLoading] = useState(false);
    const { t, i18n } = useTranslation();

    const handleDateChangeNew = (newValue, type) => {
        const d = newValue;
        const updated =
            type === 'year'
                ? new Date(Date.UTC(d.getFullYear(), date.getMonth(), 1))
                : new Date(Date.UTC(date.getFullYear(), d.getMonth(), 1));

        setDate(updated);
    };

    const handleDownloadPdf = async () => {
        setLoading(true);
        try {
            const year = date.getFullYear();
            const month = date.getMonth() + 1; // Convert to 1-indexed for API

            // Create filter to include accountant and all their subaccountants
            const filter = {
                accountantId: user.accountantId,
                userIds: [], // Empty array will fetch all users under this accountant
                startUtc: new Date(year, month - 1, 1),
                endUtc: new Date(year, month, 0)
            };

            const response = await AttendanceService.ExportDetailedPdfReportForAllUsers(
                token,
                filter,
                user.accountantId,
                year,
                month
            );

            if (response.ok) {
                const blob = await response.blob();
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;

                const paddedMonth = String(month).padStart(2, '0');
                a.download = `דוח_נוכחות_מפורט_${year}_${paddedMonth}.pdf`;

                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                window.URL.revokeObjectURL(url);

                Swal.fire({
                    title: t('Success') || 'Success!',
                    text: t('ReportDownloadedSuccessfully') || 'Report downloaded successfully.',
                    icon: 'success',
                    timer: 2000,
                    showConfirmButton: false
                });
            } else {
                throw new Error('Failed to generate PDF report');
            }
        } catch (err) {
            console.error("PDF download failed:", err);
            Swal.fire({
                title: t('Error') || 'Error',
                text: t('FailedToDownloadReport') || 'Failed to download report. Please try again.',
                icon: 'error',
                confirmButtonText: t('OK') || 'OK'
            });
        } finally {
            setLoading(false);
        }
    };

    const handleDownloadExcel = async () => {
        setLoading(true);
        try {
            const year = date.getFullYear();
            const month = date.getMonth() + 1; // Convert to 1-indexed for API

            // Create filter to include accountant and all their subaccountants
            const filter = {
                accountantId: user.accountantId,
                userIds: [], // Empty array will fetch all users under this accountant
                startUtc: new Date(year, month - 1, 1),
                endUtc: new Date(year, month, 0)
            };

            const response = await AttendanceService.ExportExcelReportForAllUsers(
                token,
                filter,
                user.accountantId,
                year,
                month
            );

            if (response.ok) {
                const blob = await response.blob();
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;

                const paddedMonth = String(month).padStart(2, '0');
                a.download = `דוח_נוכחות_${year}_${paddedMonth}.xlsx`;

                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                window.URL.revokeObjectURL(url);

                Swal.fire({
                    title: t('Success') || 'Success!',
                    text: t('ReportDownloadedSuccessfully') || 'Report downloaded successfully.',
                    icon: 'success',
                    timer: 2000,
                    showConfirmButton: false
                });
            } else {
                throw new Error('Failed to generate Excel report');
            }
        } catch (err) {
            console.error("Excel download failed:", err);
            Swal.fire({
                title: t('Error') || 'Error',
                text: t('FailedToDownloadReport') || 'Failed to download report. Please try again.',
                icon: 'error',
                confirmButtonText: t('OK') || 'OK'
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth dir={direction}>
            <DialogTitle
                dir={direction}
                sx={{
                    background: 'linear-gradient(135deg, #5b6ee0 0%, #6f45a0 100%)',
                    color: 'white',
                    py: 2
                }}>
                {t('DownloadAttendanceReport') || 'Download Attendance Report'}
            </DialogTitle>
            <DialogContent sx={{ mt: 2 }}>
                <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" sx={{
                        color: 'text.secondary',
                        mb: 2,
                        textAlign: direction === 'rtl' ? 'right' : 'left'
                    }}>
                        {t('SelectMonthForReport') || 'Select the month and year for the report. The report will include attendance data for the accountant and all sub-accountants.'}
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
                    <CustomMonthYearIndividualDatePicker
                        year={date}
                        month={date}
                        onYearChange={(newYear) => handleDateChangeNew(newYear, 'year')}
                        onMonthChange={(newMonth) => handleDateChangeNew(newMonth, 'month')}
                        labelYear={t('Year')}
                        labelMonth={t('Month')}
                        height="40px"
                        widthOuter='270px'
                        widthInner='120px'
                        flip={true}
                    />
                </Box>
                <Box sx={{
                    display: 'flex',
                    gap: 2,
                    justifyContent: 'center',
                    mt: 3
                }}>
                    <Button
                        variant="contained"
                        startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <PdfIcon />}
                        onClick={handleDownloadPdf}
                        disabled={loading}
                        sx={{
                            background: 'linear-gradient(135deg, #f44336 0%, #d32f2f 100%)',
                            color: 'white',
                            px: 3,
                            py: 1.5,
                            '&:hover': {
                                background: 'linear-gradient(135deg, #d32f2f 0%, #c62828 100%)',
                            },
                            '&:disabled': {
                                background: 'rgba(0, 0, 0, 0.12)'
                            }
                        }}
                    >
                        {t('DownloadPDF') || 'Download PDF'}
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <ExcelIcon />}
                        onClick={handleDownloadExcel}
                        disabled={loading}
                        sx={{
                            background: 'linear-gradient(135deg, #4caf50 0%, #388e3c 100%)',
                            color: 'white',
                            px: 3,
                            py: 1.5,
                            '&:hover': {
                                background: 'linear-gradient(135deg, #388e3c 0%, #2e7d32 100%)',
                            },
                            '&:disabled': {
                                background: 'rgba(0, 0, 0, 0.12)'
                            }
                        }}
                    >
                        {t('DownloadExcel') || 'Download Excel'}
                    </Button>
                </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2 }}>
                <Button onClick={onClose} disabled={loading}>
                    {t('Close') || 'Close'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
